import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import {
  Sparkle, WhatsappLogo, FacebookLogo, InstagramLogo, Copy, CheckCircle,
  ArrowsClockwise, ArrowRight, House, Confetti, Info, Image as ImageIcon,
} from "@phosphor-icons/react";
import { FlyerModal } from "./FlyerModal";

/**
 * Publish Success Share — Fase 5.3
 * Se muestra tras publicar exitosamente una propiedad. Genera un texto
 * comercial breve editable y ofrece opciones de compartir (WhatsApp,
 * Facebook, Instagram, Copiar). Nunca abre una red social ni comparte sin
 * la confirmación explícita del asesor.
 */
export function PublishSuccessShare({ property, onClose }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");
  const [text, setText]       = useState("");
  const [fallback, setFallback] = useState(false);
  const [confirming, setConfirming] = useState(null); // 'whatsapp' | 'facebook' | 'instagram' | null
  const [dirty, setDirty] = useState(false);
  const [flyerOpen, setFlyerOpen] = useState(false);

  const publicUrl = property?.id
    ? `${window.location.origin}/propiedades/${property.id}`
    : "";
  // URL para compartir en mensajería / redes: apunta al endpoint OG del backend
  // para que WhatsApp, Facebook, Messenger y el share sheet nativo muestren la
  // foto principal como preview en lugar del logo genérico de la SPA.
  const socialUrl = property?.id
    ? `${window.location.origin}/api/share/property/${property.id}?utm_source=publish&utm_medium=share`
    : "";
  // Alias para el botón Facebook (retrocompatibilidad con el nombre anterior).
  const facebookUrl = socialUrl;

  const generate = async () => {
    if (!property?.id) return;
    setLoading(true); setError(""); setFallback(false);
    try {
      const { data } = await api.post("/atlas/generate", {
        kind: "share",
        property_id: property.id,
      });
      const first = (data?.drafts || [])[0] || "";
      setText(first);
      setFallback(Boolean(data?.fallback));
      setDirty(false);
    } catch (err) {
      setError(formatApiError(err, "ATLAS no pudo preparar el texto para compartir."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { generate(); /* eslint-disable-next-line */ }, [property?.id]);

  const composedText = text?.trim() ? `${text.trim()}${socialUrl ? `\n${socialUrl}` : ""}` : socialUrl;

  const openWhatsApp = () => {
    const encoded = encodeURIComponent(composedText);
    window.open(`https://wa.me/?text=${encoded}`, "_blank", "noopener,noreferrer");
    toast.success("Abriendo WhatsApp para compartir");
    setConfirming(null);
  };
  const openFacebook = async () => {
    // Facebook Sharer no permite prellenar texto de forma confiable, así que
    // copiamos el texto al portapapeles y abrimos el diálogo con la URL
    // que expone las OG tags con la foto principal.
    try { await navigator.clipboard.writeText(composedText); } catch { /* noop */ }
    const url = facebookUrl || publicUrl || window.location.origin;
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      "_blank", "noopener,noreferrer,width=600,height=520"
    );
    toast.success("Texto copiado. Pégalo en la publicación de Facebook.");
    setConfirming(null);
  };
  const copyForInstagram = async () => {
    try {
      await navigator.clipboard.writeText(composedText);
      toast.success("Texto copiado. Abre Instagram y pégalo al crear tu publicación.");
    } catch { toast.error("No se pudo copiar el texto"); }
    setConfirming(null);
  };
  const copyOnly = async () => {
    try {
      await navigator.clipboard.writeText(composedText);
      toast.success("Texto copiado al portapapeles");
    } catch { toast.error("No se pudo copiar el texto"); }
  };

  const goDetail   = () => { onClose?.(); navigate(`/propiedades/${property.id}`); };
  const goDashboard = () => { onClose?.(); navigate("/dashboard"); };

  const ChannelButton = ({ icon: Icon, label, testId, onClick, tone = "gold" }) => (
    <button
      onClick={onClick}
      className={`group flex flex-col items-center justify-center gap-1.5 rounded-xl border p-3 sm:p-4 transition-colors ${
        tone === "gold"
          ? "border-[#C8A24A]/30 bg-[#151515] hover:border-[#C8A24A]/60 hover:bg-[#1A1A1A]"
          : "border-[#C8A24A]/20 bg-[#0F0F0F] hover:border-[#C8A24A]/40"
      }`}
      data-testid={testId}
    >
      <Icon size={22} weight="duotone" className="text-[#C8A24A] group-hover:scale-110 transition-transform" />
      <span className="text-xs font-semibold text-[#C8A24A]">{label}</span>
    </button>
  );

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
      role="dialog"
      aria-modal="true"
      data-testid="publish-success-share"
    >
      <div className="w-full sm:max-w-2xl bg-[#0F0F0F] border border-[#C8A24A]/30 rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[94vh] overflow-hidden flex flex-col">
        {/* Header — celebration */}
        <div className="p-5 sm:p-6 border-b border-[#C8A24A]/15 bg-gradient-to-br from-[#C8A24A]/10 via-[#0F0F0F] to-[#0F0F0F]">
          <div className="flex items-center gap-2">
            <Confetti size={16} weight="fill" className="text-[#C8A24A]" />
            <p className="text-xs uppercase tracking-widest text-[#C8A24A] font-semibold">
              Publicación exitosa
            </p>
          </div>
          <h2 className="font-heading text-2xl sm:text-3xl mt-2 tracking-tight" data-testid="publish-success-title">
            ¡Propiedad publicada!
          </h2>
          <p className="text-sm text-[#C8A24A]/70 mt-1.5 leading-relaxed">
            Tu propiedad ya está activa. ¿Quieres compartirla ahora para llegar a más compradores?
          </p>
        </div>

        {/* Body — share preview */}
        <div className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-4">
          <div className="rounded-xl border border-[#C8A24A]/25 bg-[#151515] p-4">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
              <div className="flex items-center gap-2">
                <Sparkle size={13} weight="fill" className="text-[#C8A24A]" />
                <p className="text-xs uppercase tracking-widest text-[#C8A24A]/80 font-semibold">
                  ATLAS · Texto para compartir
                </p>
              </div>
              <Button
                onClick={generate}
                variant="outline"
                size="sm"
                disabled={loading}
                className="h-8 text-xs"
                data-testid="publish-share-regenerate"
              >
                <ArrowsClockwise size={12} className="mr-1.5" /> Regenerar
              </Button>
            </div>

            {loading ? (
              <div className="animate-pulse space-y-2" data-testid="publish-share-loading">
                <div className="h-3 bg-[#0A0A0A] rounded w-full" />
                <div className="h-3 bg-[#0A0A0A] rounded w-11/12" />
                <div className="h-3 bg-[#0A0A0A] rounded w-3/4" />
              </div>
            ) : error ? (
              <div className="text-center py-3" data-testid="publish-share-error">
                <p className="text-sm text-red-300">{error}</p>
                <Button onClick={generate} variant="outline" size="sm" className="mt-2 h-8" data-testid="publish-share-retry">
                  <ArrowsClockwise size={12} className="mr-1.5" /> Reintentar
                </Button>
              </div>
            ) : (
              <>
                <Textarea
                  value={text}
                  onChange={(e) => { setText(e.target.value); setDirty(true); }}
                  rows={6}
                  className="bg-[#0A0A0A] border-[#C8A24A]/20 text-sm resize-y"
                  data-testid="publish-share-textarea"
                  placeholder="Escribe o edita el texto para compartir..."
                />
                {socialUrl && (
                  <p className="mt-2 text-xs text-[#C8A24A]/50 break-all" data-testid="publish-share-url">
                    Se añadirá el enlace: {socialUrl}
                  </p>
                )}
                {fallback && !dirty && (
                  <div className="mt-2 flex items-start gap-1.5 text-xs text-[#C8A24A]/70">
                    <Info size={12} weight="fill" className="flex-shrink-0 mt-0.5" />
                    <span>Texto base con los datos disponibles. Puedes editarlo antes de compartir.</span>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Channels */}
          <div>
            <p className="text-xs uppercase tracking-widest text-[#C8A24A]/60 font-semibold mb-2">
              Compartir en
            </p>
            <div className="grid grid-cols-4 gap-2 sm:gap-3">
              <ChannelButton
                icon={WhatsappLogo}
                label="WhatsApp"
                testId="publish-share-whatsapp"
                onClick={() => setConfirming("whatsapp")}
              />
              <ChannelButton
                icon={FacebookLogo}
                label="Facebook"
                testId="publish-share-facebook"
                onClick={() => setConfirming("facebook")}
              />
              <ChannelButton
                icon={InstagramLogo}
                label="Instagram"
                testId="publish-share-instagram"
                onClick={() => setConfirming("instagram")}
              />
              <ChannelButton
                icon={Copy}
                label="Copiar texto"
                testId="publish-share-copy"
                onClick={copyOnly}
              />
            </div>
            <p className="mt-2 text-xs text-[#C8A24A]/50 leading-relaxed">
              Ninguna red social se abrirá hasta que confirmes. Nada se comparte automáticamente.
            </p>
          </div>

          {/* Flyer CTA */}
          <div className="rounded-xl border border-[#C8A24A]/30 bg-gradient-to-br from-[#C8A24A]/10 to-[#151515] p-3 sm:p-4" data-testid="publish-success-flyer-cta">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#C8A24A]/15 border border-[#C8A24A]/30 flex items-center justify-center flex-shrink-0">
                <ImageIcon size={20} weight="duotone" className="text-[#C8A24A]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[#C8A24A]">Crear flyer</p>
                <p className="text-xs text-[#C8A24A]/60 leading-snug">
                  Plantilla luxury cuadrada, lista para redes. Foto original intacta.
                </p>
              </div>
              <Button
                onClick={() => setFlyerOpen(true)}
                className="bg-[#C8A24A] hover:bg-[#B88C2D] text-[#111111] h-9 flex-shrink-0"
                data-testid="publish-success-flyer-btn"
              >
                Crear flyer
              </Button>
            </div>
          </div>
        </div>

        {/* Footer actions */}
        <div className="p-4 sm:p-5 border-t border-[#C8A24A]/15 flex flex-wrap items-center gap-2 justify-between">
          <Button
            variant="outline"
            onClick={goDashboard}
            className="h-10"
            data-testid="publish-success-dashboard"
          >
            Volver al panel
          </Button>
          <Button
            onClick={goDetail}
            className="bg-[#C8A24A] hover:bg-[#B88C2D] text-[#111111] h-10"
            data-testid="publish-success-detail"
          >
            Ver propiedad <ArrowRight size={14} className="ml-1.5" />
          </Button>
        </div>
      </div>

      {/* Confirmation overlay */}
      {confirming && (
        <div className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-4" role="dialog" aria-modal="true" data-testid="publish-share-confirm">
          <div className="w-full max-w-sm bg-[#0F0F0F] border border-[#C8A24A]/40 rounded-2xl p-5 shadow-2xl">
            <p className="text-xs uppercase tracking-widest text-[#C8A24A]/70 font-semibold">
              Confirmar
            </p>
            <h3 className="font-heading text-lg mt-1.5" data-testid="publish-share-confirm-title">
              {confirming === "whatsapp"  && "Compartir por WhatsApp"}
              {confirming === "facebook"  && "Compartir en Facebook"}
              {confirming === "instagram" && "Preparar para Instagram"}
            </h3>
            <p className="text-sm text-[#C8A24A]/70 mt-2 leading-relaxed">
              {confirming === "whatsapp" && "Se abrirá WhatsApp en una nueva pestaña con el texto listo para que elijas a quién enviar."}
              {confirming === "facebook" && "Se copiará el texto y se abrirá el diálogo de Facebook. Pega el texto al publicar."}
              {confirming === "instagram" && "Instagram no permite compartir directo desde web. Copiaremos el texto para que lo pegues en tu publicación."}
            </p>
            {confirming === "facebook" && (
              <p className="text-xs text-[#C8A24A]/60 mt-2 leading-relaxed border-t border-[#C8A24A]/15 pt-2" data-testid="fb-photo-note">
                Facebook mostrará automáticamente la foto principal de la propiedad. Para publicar varias fotos, agrégalas manualmente desde Facebook.
              </p>
            )}
            <div className="mt-4 flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setConfirming(null)} className="h-9" data-testid="publish-share-confirm-cancel">
                Cancelar
              </Button>
              <Button
                onClick={() => {
                  if (confirming === "whatsapp") openWhatsApp();
                  else if (confirming === "facebook") openFacebook();
                  else copyForInstagram();
                }}
                className="bg-[#C8A24A] hover:bg-[#B88C2D] text-[#111111] h-9"
                data-testid="publish-share-confirm-ok"
              >
                <CheckCircle size={14} className="mr-1.5" /> Confirmar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Flyer Modal — Fase 6 */}
      {flyerOpen && (
        <FlyerModal
          open={flyerOpen}
          property={property}
          onClose={() => setFlyerOpen(false)}
        />
      )}
    </div>
  );
}
